import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Client } from '../models/Client';
import type { Offer, OfferLine, OfferWithLines } from '../models/Offer';
import { OfferStatus } from '../models/enums';
import { offerService } from '../services/dataverse/offerService';

export const useOffers = () => {
  const [allOffers, setAllOffers] = useState<Offer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<OfferStatus | 'all'>('all');

  const loadOffers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setAllOffers(await offerService.list());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load offers.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadOffers(); }, [loadOffers]);

  // Client-side filtering — no API call on every keystroke
  const offers = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allOffers
      .filter((o) => status === 'all' || o.cr_crm_status === status)
      .filter((o) => !term || [
        o.cr_crm_reference,
        o.cr_crm_client?.cr_crm_companyname,
      ].filter(Boolean).some((v) => v?.toLowerCase().includes(term)));
  }, [allOffers, search, status]);

  const getOffer = useCallback((id: string) => offerService.getById(id), []);
  const createDraftTemplate = useCallback((client?: Client, exchangeRate?: number, taxRate?: number) => offerService.createDraftTemplate(client, exchangeRate, taxRate), []);
  const saveOffer = useCallback(async (offer: Offer, lines: OfferLine[]): Promise<OfferWithLines> => {
    const saved = await offerService.save(offer, lines);
    await loadOffers();
    return saved;
  }, [loadOffers]);
  const deleteOffer = useCallback(async (id: string) => { await offerService.delete(id); await loadOffers(); }, [loadOffers]);
  const sendOffer = useCallback((id: string, recipientEmail: string, message: string, cc?: string) => offerService.send(id, recipientEmail, message, cc), []);
  const getPreviewHtml = useCallback((offer: Offer, lines: OfferLine[]) => offerService.getPreviewHtml(offer, lines), []);

  return { offers, loading, error, search, setSearch, status, setStatus, reload: loadOffers, getOffer, createDraftTemplate, saveOffer, deleteOffer, sendOffer, getPreviewHtml };
};
